/**
 * Is this the app in a browser, or the app on a till?
 *
 * The desktop shell puts a small object on the window with the two things a browser cannot
 * do: bytes on a thermal printer without a dialog, and a cash drawer. Everything else is
 * identical, so the rest of the app should not have to care which it is running on. It asks
 * here, and gets either the real thing or null.
 */
const shell = typeof window !== 'undefined' && window.epms?.isTill ? window.epms : null;

export const onTill = Boolean(shell);
export const till = shell;

/**
 * Print a receipt the best way this machine can.
 *
 * On a till that means ESC/POS straight down the wire, with no dialog and no drawer. In a
 * browser it means the print dialog and the 80mm layout on screen, which is what every
 * manager reviewing a sale from a laptop should get.
 *
 * Returns { silent, sent, error } so the caller can say what actually happened. It never
 * throws: a receipt that will not print must not lose the sale that is already on the books.
 */
export async function printReceipt(sale, { fallback } = {}) {
  if (!shell) {
    fallback?.();
    return { silent: false, sent: true };
  }

  /*
   * The drawer stays shut.
   *
   * It used to open by itself on a cash sale, which reads well and is wrong in a salon: the
   * cashier is often not at the drawer when the receipt prints, and a drawer that opens with
   * nobody in front of it is a drawer standing open. Change is given when the customer is
   * there, not when the paper comes out, so the person decides and presses the button.
   */
  try {
    const r = await shell.printReceipt(sale, { openDrawer: false });
    if (!r.sent && fallback) fallback();
    return { silent: true, ...r };
  } catch (e) {
    fallback?.();
    return { silent: true, sent: false, error: e.message };
  }
}

/** The No sale button. Only exists on a till. */
export async function openDrawer(settings) {
  if (!shell) return { sent: false, error: 'not_a_till' };
  try {
    return await shell.openDrawer(settings);
  } catch (e) {
    return { sent: false, error: e.message };
  }
}

/** The ruler page, printed from this machine rather than from the server in the rack. */
export async function testPrint(settings) {
  if (!shell) return { sent: false, error: 'not_a_till' };
  try {
    return await shell.testPrint(settings);
  } catch (e) {
    return { sent: false, error: e.message };
  }
}

/**
 * Printer settings live in two places on purpose. The server holds them so a super admin can
 * see how a till is set up without walking to Oyarifa; the machine holds them because it is
 * the machine that has to reach the printer, and it must keep working when the server does
 * not. The setup screen writes both, and the machine's copy wins locally.
 */
export const localSettings = () => shell ? shell.getSettings() : Promise.resolve(null);
export const saveLocalSettings = patch => shell ? shell.saveSettings(patch) : Promise.resolve(null);
export const pair = code => shell ? shell.pair(code) : Promise.resolve({ paired: false, error: 'not_a_till' });
export const version = () => shell ? shell.version() : Promise.resolve(null);

/** The printers Windows knows about on this machine, and which of them are shared. */
/**
 * Whether this shell can do a thing.
 *
 * The web app updates itself from the server whenever the till is online; the desktop shell
 * only changes when somebody reinstalls. So the page is routinely newer than the shell it is
 * running in, and calling a method the shell has never heard of is not a rare edge, it is the
 * normal state of a till between installs.
 *
 * Every feature reached through window.epms therefore has to be asked about first, and have
 * somewhere to fall back to. Doing nothing is the one outcome that is never acceptable: a
 * button that silently does nothing is worse than one that is absent, because somebody
 * presses it again.
 */
export const can = feature => Boolean(shell && typeof shell[feature] === 'function');

/** Save the current page as a PDF file. Only a till can; a browser has no file system. */
export const savePdf = opts => can('savePdf')
  ? shell.savePdf(opts)
  : Promise.resolve({ saved: false, error: 'shell_too_old' });

export const reveal = path => can('reveal') ? shell.reveal(path) : Promise.resolve(false);

/**
 * Whether the printer is actually taking the work.
 *
 * "Sent" means the spooler accepted the job. A printer that is paused, marked offline, or on
 * the wrong port accepts everything and prints nothing, so the till needs to be able to look
 * behind its own success message.
 */
export const printerQueue = name => can('printerQueue')
  ? shell.printerQueue(name)
  : Promise.resolve({ supported: false });

export const clearQueue = name => can('clearQueue')
  ? shell.clearQueue(name)
  : Promise.resolve({ cleared: 0, supported: false });

/** In plain words, what is wrong with a queue that is not moving. */
export function queueProblem(q) {
  if (!q || !q.supported || q.error) return null;
  if (q.found === false) return `Windows has no printer called ${q.printer} any more.`;
  if (q.offline) return `${q.printer} is set to "use printer offline" in Windows, so it accepts `
    + 'everything and prints nothing. Untick it in the print queue window.';
  if (q.paused) return `${q.printer} is paused in Windows. Resume it in the print queue window.`;
  if (q.queued > 3) return `${q.queued} jobs are waiting at ${q.printer} and not printing. `
    + 'The printer is probably off, out of paper, or on the wrong port.';
  return null;
}

export const printers = () => can('printers')
  ? shell.printers()
  // A shell that predates this cannot ask Windows. Said out loud, because a picker that is
  // simply absent looks like a machine with no printers on it.
  : Promise.resolve({ platform: null, printers: [], shellTooOld: onTill });

/**
 * Whether the server is reachable and how many sales are waiting to go up.
 *
 * Subscribed to rather than polled, because the main process already knows the moment a
 * request fails and a cashier watching a queue drain should see it drain.
 */
export function watchConnection(fn) {
  if (!can('onSync') || !can('status')) return () => {};
  shell.status().then(fn).catch(() => {});
  return shell.onSync(() => shell.status().then(fn).catch(() => {}));
}

/** Send what is waiting now, because somebody is standing there wanting to know. */
export const syncNow = () => can('syncNow') ? shell.syncNow() : Promise.resolve(null);

/** Plain English for what came back, for a cashier who cannot read an error code. */
export function printerProblem(result) {
  if (!result || result.sent) return null;
  switch (result.error) {
    case 'no_printer_configured': return 'This till has no printer set up yet.';
    case 'unreachable': return 'The printer did not answer. Check it is on and connected.';
    case 'no_such_share': return 'Windows has no shared printer by that name on this machine.';
    case 'timeout': return 'The printer took too long to answer.';
    case 'not_a_till': return 'Receipts print from the till machine, not from a browser.';
    default: {
      const e = String(result.error || '');
      // A transport the shell has never heard of means the screens are newer than the app
      // around them, which is the normal state of a till between installs and says nothing
      // useful on its own.
      if (e.startsWith('unknown_transport')) {
        return 'This copy of the till app does not know that kind of printer connection. '
          + 'Open This machine and either pick a different one or install the current version.';
      }
      if (/no_such_printer/.test(e)) {
        return 'Windows has no printer by that name on this machine. Open This machine and pick '
          + 'one from the list rather than typing it.';
      }
      if (/ENOENT|no such file|cannot find/i.test(e)) {
        return 'Windows could not find that printer share. Open This machine and pick the printer '
          + 'from the list.';
      }
      return `The printer refused the receipt: ${e || 'unknown reason'}`;
    }
  }
}
